import ConfirmDialog from './ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import api from '../services/api';
import RecurringConfirmationModal from './RecurringConfirmationModal';
import StopRecurringModal from './StopRecurringModal';

// Sub-session C: pricing visibility hook
import useUserPermissions from '../hooks/useUserPermissions';
import { calculateMonthDates, formatDateForDisplay } from '../utils/dateRecurrence';

const STATUS_COLORS = {
  active:    { color: '#B71C1C', bg: '#ffebee', label: 'Activity' },
  clean:     { color: '#2D5C3F', bg: '#e8f5e9', label: 'Clean' },
  no_access: { color: '#E65100', bg: '#fff3e0', label: 'No Access' },
  exception: { color: '#6A1B9A', bg: '#f3e5f5', label: 'Exception' },
  pending:   { color: '#999',    bg: '#f5f5f5', label: 'Pending' },
};
const NODE_ICONS = { building: '🏢', unit: '🏗️', area: '📍', device: '🪤' };
const DeviceTree = ({ nodes, level }) => (
  <div style={{ marginLeft: level * 16 }}>
    {nodes.map(node => {
      const sc = STATUS_COLORS[node.inspection_status] || STATUS_COLORS.pending;
      return (
        <div key={node.id}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 8px', borderRadius: 6, marginBottom: 2,
            background: level === 0 ? '#f0f4f0' : 'transparent',
            borderLeft: node.node_type === 'device' ? '3px solid ' + sc.color : 'none'
          }}>
            <span style={{ fontSize: 14 }}>{NODE_ICONS[node.node_type] || '📌'}</span>
            <span style={{ fontSize: 13, fontWeight: node.node_type !== 'device' ? 600 : 400, color: '#333', flex: 1 }}>
              {node.name}
              {node.device_type_name && <span style={{ color: '#888', fontWeight: 400 }}> · {node.device_type_name}</span>}
              {node.barcode && <span style={{ color: '#aaa', fontSize: 11 }}> #{node.barcode}</span>}
            </span>
            {node.node_type === 'device' && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color: sc.color, background: sc.bg }}>
                {sc.label}
              </span>
            )}
          </div>
          {node.children && node.children.length > 0 && (
            <DeviceTree nodes={node.children} level={level + 1} />
          )}
        </div>
      );
    })}
  </div>
);

const ServiceOrderModal = ({ 
  isOpen, 
  onClose, 
  order, 
  facilityId, 
  facilityName, 
  clientName,
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    facility_id: facilityId,
    technician_id: '',
    service_type: 'routine',
    scheduled_date: '',
    scheduled_time_start: '09:00',
    scheduled_time_end: '11:00',
    estimated_duration: 120,
    priority: 'normal',
    notes: ''
  });

  const [technicians, setTechnicians] = useState([]);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState({
    recurrence_pattern: 'weekly',
    day_of_week: 1,
    biweekly_option: '1_3',
    week_of_month: 1,
    // Sub-session D Step 17: extended for daily/2x_week/3x_week and bimonthly/quarterly/semiannual
    days_of_week: [1, 2, 3, 4, 5], // default Mon-Fri (JS: 0=Sun..6=Sat). Used by daily/2x/3x patterns.
    day_of_month: 1,                // default day 1. Used by bimonthly/quarterly/semiannual.
    recurrence_start_date: '',
    end_date: '',
    fifth_week_vip: false
  });
  const [previewDates, setPreviewDates] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Bug-1 followup: confirm summary modal for punctual new WOs (parity with recurring preview)
  const [showPunctualConfirm, setShowPunctualConfirm] = useState(false);
  const [currentMonthDates, setCurrentMonthDates] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  // ── D.27a Step Routing (sequential modal flow) ───────────────────────────
  const [currentStep, setCurrentStep] = useState(1);
  // ── D.27d First visit display for recurring (auto-computed from pattern + start_date) ──
  const [firstVisitFormatted, setFirstVisitFormatted] = useState('');
  // ── D.27c Step navigation helpers ──
  // Bug-1 fix: TOTAL_STEPS = 3 (final, design-reviewed). Step 1 = Service Type+Priority,
  // Step 2 = Catalog+Recurring Config, Step 3 = catch-all (Schedule+Tech+Pricing+Notes) + Save.
  // Previously was 5 but catch-all (>= 3) served identical content for steps 3,4,5 (no real
  // Review screen existed), causing double-click to reveal Save. If a true Review screen is
  // added later, that becomes a new step and TOTAL_STEPS bumps back up.
  const TOTAL_STEPS = 3;
  const getNextStep = (curr) => Math.min(curr + 1, TOTAL_STEPS);
  const getPrevStep = (curr) => Math.max(curr - 1, 1);
  // ── Pricing state (Sub-session C) ────────────────────────────────────────
  const { canViewPricing } = useUserPermissions();
  const [woType, setWoType] = useState('service_punctual');
  const [servicePrice, setServicePrice] = useState('');
  const [lineItems, setLineItems] = useState([]); // [{description, quantity, unit_price}]
  // ── Catalog selector (Sub-session D Step 12) ─────────────────────────────
  const [catalogItems, setCatalogItems] = useState([]); // punctual items from /api/service-catalog
  const [catalogItemId, setCatalogItemId] = useState(''); // empty string = manual entry
  // ── D.32: Dual-mode pricing for recurring WOs ────────────────
  // pricingMode: '' (not picked) | 'total' (Monthly/Annual Total) | 'individual' (per-visit)
  const [pricingMode, setPricingMode] = useState('');
  // Reset pricingMode when WO becomes non-recurring (e.g., catalog changes)
  useEffect(() => { if (!isRecurring) setPricingMode(''); }, [isRecurring]);
  // D.32 fix3: On edit of existing recurring WO, default to 'individual' mode
  // because the stored DB value is already per-visit (per-WO price). User can
  // still switch to 'total' mode to enter a new contract total if needed.
  useEffect(() => {
    if (order && isRecurring && order.service_price != null) {
      setPricingMode('individual');
    }
  }, [order, isRecurring]);
  // Paid visits per period for recurring patterns.
  // Returns visits/MONTH for intra-monthly patterns; visits/YEAR for multi-monthly.
  function getPaidVisitsCount(pattern, daysOfWeek) {
    const dowCount = Array.isArray(daysOfWeek) && daysOfWeek.length > 0 ? daysOfWeek.length : 5;
    switch (pattern) {
      case 'daily':       return dowCount * 4;
      case '2x_week':     return 8;
      case '3x_week':     return 12;
      case 'weekly':      return 4;
      case 'biweekly':    return 2;
      case 'monthly':     return 1;
      case 'bimonthly':   return 6;   // /year
      case 'quarterly':   return 4;   // /year
      case 'semiannual':  return 2;   // /year
      case 'annual':      return 1;   // /year
      default:            return 1;
    }
  }
  function isIntraMonthlyPattern(pattern) {
    if (!pattern) return true;
    return ['daily', '2x_week', '3x_week', 'weekly', 'biweekly', 'monthly'].includes(pattern);
  }
  // ── Upgrade flag (Sub-session D Step 13) ─────────────────────────────────
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [clientHasRecurring, setClientHasRecurring] = useState(false);
  // ── Stop Recurring modal (Sub-session D Step 14c) ────────────────────────
  const [showStopModal, setShowStopModal] = useState(false);
  const [recurringServiceData, setRecurringServiceData] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deviceTree, setDeviceTree] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
      setHistoryLogs([]);
      fetchTechnicians();
      // Hydrate pricing fields from existing order (Sub-session C)
      if (order) {
        setWoType(order.wo_type || 'service_punctual');
        setServicePrice(order.service_price != null ? String(order.service_price) : '');
        // line_items are fetched on-demand from /api/service-orders/:id/line-items
        // when the user expands the section; for now leave empty and load lazily.
        setLineItems([]);
        // Sub-session D: hydrate catalog selection
        setCatalogItemId(order.catalog_item_id != null ? String(order.catalog_item_id) : '');
      } else {
        setWoType('service_punctual');
        setServicePrice('');
        setLineItems([]);
        setCatalogItemId('');
      }
      if (order) {
        setFormData({
          facility_id: order.facility_id,
          technician_id: order.technician_id || '',
          service_type: order.service_type,
          scheduled_date: order.scheduled_date,
          scheduled_time_start: order.scheduled_time_start,
          scheduled_time_end: order.scheduled_time_end,
          estimated_duration: calculateDuration(order.scheduled_time_start, order.scheduled_time_end),
          priority: order.priority,
          notes: order.notes || ''
        });
      }
    }
  }, [isOpen, order]);

  useEffect(() => {
    if (isRecurring && recurringConfig.recurrence_start_date) {
      calculatePreviewDates();
    } else {
      setPreviewDates([]);
    }
  }, [isRecurring, recurringConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAvailability();
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.technician_id, formData.scheduled_date, formData.scheduled_time_start, formData.scheduled_time_end]);

  // Fetch catalog items (Sub-session D Step 12 + 18: now includes BOTH recurring and punctual)
  useEffect(() => {
    if (!isOpen) return;
    const fetchCatalog = async () => {
      try {
        const res = await api.get('/service-catalog');
        setCatalogItems(res.data.items || []);
      } catch (err) {
        console.warn('Failed to load catalog:', err.message);
        setCatalogItems([]);
      }
    };
    fetchCatalog();
  }, [isOpen]);

  // Detect if facility already has an active non-upgrade recurring (Sub-session D Step 13)
  useEffect(() => {
    if (!isOpen || !formData.facility_id) {
      setClientHasRecurring(false);
      return;
    }
    const detect = async () => {
      try {
        const res = await api.get('/recurring', { params: { facility_id: formData.facility_id } });
        const arr = Array.isArray(res.data) ? res.data : [];
        const hasBase = arr.some(rs => rs.is_active && rs.is_upgrade !== true);
        setClientHasRecurring(hasBase);
        if (!order) {
          setIsUpgrade(hasBase);
        }
        // Sub-session D Step 14c: when editing a WO that is an upgrade,
        // grab the matching recurring service so we can pass it to StopRecurringModal.
        if (order && order.recurring_service_id) {
          const matched = arr.find(rs => rs.id === order.recurring_service_id);
          if (matched) {
            setRecurringServiceData({
              id: matched.id,
              service_type: matched.service_type,
              facility_name: order.facility_name || matched.facility_name,
              client_name: order.client_name || matched.client_name,
            });
          }
        }
      } catch (err) {
        console.warn('Failed to detect facility recurring:', err.message);
        setClientHasRecurring(false);
      }
    };
    detect();
  }, [isOpen, formData.facility_id, order]);

  const calculateDuration = (start, end) => {
    if (!start || !end) return 120;
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let duration = (endH * 60 + endM) - (startH * 60 + startM);
    if (duration <= 0) duration += 24 * 60; // overnight: add 24 hours
    return duration;
  };

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/users');
      const users = response.data.data || [];
      const techs = users.filter(user => user.is_technician);
      setTechnicians(techs);
    } catch (err) {
      console.error('Error loading technicians:', err);
    }
  };

  const checkAvailability = async () => {
    if (!formData.technician_id || !formData.scheduled_date || !formData.scheduled_time_start || !formData.scheduled_time_end) {
      setAvailabilityStatus(null);
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await api.post('/service-orders/check-availability', {
        technician_id: formData.technician_id,
        scheduled_date: formData.scheduled_date,
        scheduled_time_start: formData.scheduled_time_start,
        scheduled_time_end: formData.scheduled_time_end,
        exclude_order_id: order?.id
      });

      if (response.data.available) {
        setAvailabilityStatus('available');
        setConflicts([]);
      } else {
        setAvailabilityStatus('conflict');
        setConflicts(response.data.conflicts || []);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setAvailabilityStatus(null);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleTimeStartChange = (value) => {
    const [hours, minutes] = value.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + formData.estimated_duration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    const endTime = String(endHours).padStart(2, '0') + ':' + String(endMinutes).padStart(2, '0');

    setFormData({
      ...formData,
      scheduled_time_start: value,
      scheduled_time_end: endTime
    });
  };

  // D.27d: Auto-compute first visit for recurring; sync formData.scheduled_date so availability check
  // (existing useEffect at line ~168) validates against the real first visit, not a manual date.
  useEffect(() => {
    if (!isRecurring) {
      setFirstVisitFormatted('');
      return;
    }
    if (!recurringConfig.recurrence_start_date || !recurringConfig.recurrence_pattern) {
      setFirstVisitFormatted('');
      return;
    }
    if (recurringConfig.recurrence_pattern === 'biweekly' && !recurringConfig.biweekly_option) {
      setFirstVisitFormatted('');
      return;
    }
    const config = { ...recurringConfig };
    if (config.recurrence_pattern === 'biweekly') {
      config.weeks = config.biweekly_option === '1_3' ? [1, 3] : [2, 4];
    }
    const startStr = recurringConfig.recurrence_start_date;
    const start = new Date(startStr + 'T12:00:00');
    let curYear = start.getFullYear();
    let curMonth = start.getMonth();
    let firstRaw = null;
    for (let i = 0; i < 36 && !firstRaw; i++) {
      const monthDates = calculateMonthDates(
        config.recurrence_pattern, config, curYear, curMonth, startStr
      );
      if (monthDates && monthDates.length > 0) {
        firstRaw = monthDates[0].date;
      }
      curMonth++;
      if (curMonth > 11) { curMonth = 0; curYear++; }
    }
    if (firstRaw) {
      let dateStr;
      if (firstRaw instanceof Date) {
        const yyyy = firstRaw.getFullYear();
        const mm = String(firstRaw.getMonth() + 1).padStart(2, '0');
        const dd = String(firstRaw.getDate()).padStart(2, '0');
        dateStr = yyyy + '-' + mm + '-' + dd;
      } else {
        dateStr = String(firstRaw).split('T')[0];
      }
      setFormData(prev => prev.scheduled_date === dateStr ? prev : { ...prev, scheduled_date: dateStr });
      setFirstVisitFormatted(formatDateForDisplay(firstRaw));
    } else {
      setFirstVisitFormatted('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRecurring,
    recurringConfig.recurrence_start_date,
    recurringConfig.recurrence_pattern,
    recurringConfig.days_of_week,
    recurringConfig.day_of_month,
    recurringConfig.biweekly_option,
    recurringConfig.end_date,
    recurringConfig.fifth_week_vip
  ]);

  const calculatePreviewDates = () => {
    // D.28d: now supports all 9 patterns via calculateMonthDates helper
    // (mirror of backend calculateMonthlyDates). Iterates up to 36 months
    // forward to capture 6 occurrences for any pattern, including semiannual
    // (which yields 6 dates spread over 36 months).
    if (!recurringConfig.recurrence_start_date || !recurringConfig.recurrence_pattern) {
      setPreviewDates([]);
      return;
    }
    if (recurringConfig.recurrence_pattern === 'biweekly' && !recurringConfig.biweekly_option) {
      setPreviewDates([]);
      return;
    }

    // For biweekly, derive `weeks` from biweekly_option for backend-paridad
    const config = { ...recurringConfig };
    if (config.recurrence_pattern === 'biweekly') {
      config.weeks = config.biweekly_option === '1_3' ? [1, 3] : [2, 4];
    }

    const startStr = recurringConfig.recurrence_start_date;
    const start = new Date(startStr + 'T12:00:00');
    let curYear  = start.getFullYear();
    let curMonth = start.getMonth();

    const allDates = [];
    for (let i = 0; i < 36 && allDates.length < 6; i++) {
      const monthDates = calculateMonthDates(
        config.recurrence_pattern, config, curYear, curMonth, startStr
      );
      allDates.push(...monthDates);
      curMonth++;
      if (curMonth > 11) { curMonth = 0; curYear++; }
    }

    setPreviewDates(allDates.slice(0, 6).map(d => formatDateForDisplay(d.date)));
  };

  const calculateCurrentMonthDates = () => {
    // D.28d: now supports all 9 patterns. Returns dates for the start_date's
    // month (filtered by start_date as effective range). Uses calculateMonthDates.
    if (!recurringConfig.recurrence_start_date || !recurringConfig.recurrence_pattern) return [];

    const config = { ...recurringConfig };
    if (config.recurrence_pattern === 'biweekly') {
      config.weeks = config.biweekly_option === '1_3' ? [1, 3] : [2, 4];
    }

    const startStr = recurringConfig.recurrence_start_date;
    const start = new Date(startStr + 'T12:00:00');
    const monthDates = calculateMonthDates(
      config.recurrence_pattern, config,
      start.getFullYear(), start.getMonth(), startStr
    );

    return monthDates.map(d => formatDateForDisplay(d.date));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.technician_id && availabilityStatus === 'conflict') {
      toast.error('Cannot assign order: Technician has conflicting orders during this time');
      return;
    }

    if (!order && isRecurring) {
      const monthDates = calculateCurrentMonthDates();
      setCurrentMonthDates(monthDates);
      setShowConfirmation(true);
      return;
    }

    // Bug-1 followup: punctual new WOs get a confirmation summary (parity with recurring preview)
    if (!order && !isRecurring) {
      setShowPunctualConfirm(true);
      return;
    }

    await executeCreate();
  };

  const executeCreate = async () => {
    setSubmitting(true);
    try {
      if (order) {
        await api.put(`/service-orders/${order.id}`, formData);
      } else {
        // Build payload with pricing fields for non-recurring WOs
        const payload = { ...formData };
        if (!isRecurring && canViewPricing) {
          payload.wo_type = woType;
          // Sub-session D: include catalog reference if selected
          if (catalogItemId) {
            payload.catalog_item_id = parseInt(catalogItemId, 10);
          }
          if (lineItems.length > 0) {
            // Server will compute service_price = SUM(line_items.line_total)
            payload.line_items = lineItems
              .filter(li => (li.description || '').trim() !== '')
              .map(li => ({
                description: li.description.trim(),
                quantity:   parseFloat(li.quantity) || 1,
                unit_price: parseFloat(li.unit_price) || 0,
              }));
          } else if (servicePrice !== '' && !isNaN(parseFloat(servicePrice))) {
            // D.32: Dual-mode pricing for recurring WOs
            // - 'total' mode: user entered Monthly/Annual Total -> divide by paid visits
            // - 'individual' (or punctual): user entered per-visit price directly
            // - '' mode (edit, no change): existing per-WO price carries through as-is
            if (isRecurring && pricingMode === 'total') {
              const totalPrice = parseFloat(servicePrice);
              const paidVisits = getPaidVisitsCount(recurringConfig.recurrence_pattern, recurringConfig.days_of_week);
              payload.service_price = totalPrice / paidVisits;
            } else {
              payload.service_price = parseFloat(servicePrice);
            }
          }
        }
        // D.23 fix: only POST /service-orders for non-recurring WOs.
        // For recurring WOs, the /recurring endpoint below creates both the
        // recurring_service AND its first month of WOs via the internal helper.
        if (!isRecurring) {
          await api.post('/service-orders', payload);
        }

        if (isRecurring) {
          const weeks = recurringConfig.biweekly_option === '1_3' ? [1, 3] : [2, 4];

          const recurringData = {
            facility_id: formData.facility_id,
            technician_id: formData.technician_id,
            service_type: formData.service_type,
            recurrence_pattern: recurringConfig.recurrence_pattern,
            recurrence_config: (() => {
              // Sub-session D Step 17: build config based on pattern
              const p = recurringConfig.recurrence_pattern;
              if (p === 'weekly') return { day_of_week: recurringConfig.day_of_week };
              if (p === 'biweekly') return { weeks, day_of_week: recurringConfig.day_of_week };
              if (p === 'monthly') return { week_of_month: recurringConfig.week_of_month, day_of_week: recurringConfig.day_of_week };
              if (['daily', '2x_week', '3x_week'].includes(p)) {
                return { days_of_week: recurringConfig.days_of_week || [] };
              }
              if (['bimonthly', 'quarterly', 'semiannual'].includes(p)) {
                return { day_of_month: parseInt(recurringConfig.day_of_month) || 1 };
              }
              return {};
            })(),
            start_date: recurringConfig.recurrence_start_date,
            end_date: recurringConfig.end_date || null,
            preferred_time_start: formData.scheduled_time_start,
            estimated_duration: formData.estimated_duration,
            priority: formData.priority,
            notes: formData.notes,
            // Sub-session D Step 13: indicate if this is an upgrade vs the contract base
            is_upgrade: isUpgrade === true
          };

          await api.post('/recurring', recurringData);
          toast.success('✅ Service orders created successfully!\n✅ Recurring pattern saved!\nRemember to generate orders for future months at the end of each month.');
        }
      }

      setShowConfirmation(false);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error('Error saving service order: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelOrder = () => {
    setConfirmCancel(true);
  };
  const doCancelOrder = async () => {
    setConfirmCancel(false);
    {
      try {
        await api.delete(`/service-orders/${order.id}`);
        onSuccess();
        onClose();
      } catch (err) {
        toast.error('Error cancelling order: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const fetchHistory = async () => {
    if (!order) return;
    setHistoryLoading(true);
    try {
      const res = await api.get(`/activity-log/entity/work_order/${order.id}`);
      const d = res.data || res;
      setHistoryLogs(d.data || []);
    } catch (e) { console.error(e); }
    setHistoryLoading(false);
  };
  const fetchDevices = async () => {
    if (!order) return;
    setDevicesLoading(true);
    try {
      const res = await api.get(`/facility-structure/structure/${order.facility_id}?service_order_id=${order.id}`);
      const d = res.data || res;
      setDeviceTree(d.structure || []);
    } catch (e) { console.error(e); }
    setDevicesLoading(false);
  };

  const formatTs = (ts) => !ts ? '' : new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  const ACTION_COLORS = { created:'#2D5C3F', updated:'#1565C0', completed:'#2D5C3F', started:'#E65100', cancelled:'#B71C1C', rescheduled:'#6A1B9A', reassigned:'#00695C', report_queued:'#E65100', report_released:'#2D5C3F', report_rejected:'#B71C1C' };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {order ? `Edit Service Order - ${order.order_number}` : 'Create Service Order'}
          </h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.clientInfo}>
          <strong>{clientName}</strong> → {facilityName}
        </div>
        {/* Sub-session D Step 14c: upgrade context banner */}
        {order && order.recurring_is_upgrade === true && (
          <div style={{
            padding: '12px 20px',
            background: '#FEF3C7',
            borderBottom: '2px solid #F59E0B',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                🔼 Upgrade Service
              </div>
              <div style={{ fontSize: 13, color: '#78350F' }}>
                This WO belongs to upgrade <strong>{order.recurring_service_name || 'service'}</strong>.
                You can stop the upgrade independently from the contract base.
              </div>
            </div>
            <button
              onClick={() => setShowStopModal(true)}
              disabled={!recurringServiceData}
              style={{
                padding: '8px 14px',
                background: recurringServiceData ? '#DC2626' : '#9CA3AF',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: recurringServiceData ? 'pointer' : 'not-allowed',
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              ⛔ Stop this upgrade
            </button>
          </div>
        )}
        {order && ['in_progress', 'completed'].includes(order.status) && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: order.status === 'in_progress' ? '#FFF3E0' : '#E8F5E9',
            borderBottom: '1px solid #E0E0E0',
            fontSize: '13px',
            fontWeight: 'bold',
            color: order.status === 'in_progress' ? '#E65100' : '#2E7D32'
          }}>
            {order.status === 'in_progress' ? '🔧 Service In Progress — Fields locked (technician is working)' : '✅ Service Completed — Read only'}
          </div>
        )}

        {order && (
          <div style={{display:'flex',borderBottom:'2px solid #e9ecef',background:'#f8f9fa'}}>
            {[{key:'details',label:'📋 Details'},{key:'history',label:'🕐 History'}].map(tab=>(
              <button key={tab.key} type="button"
                onClick={()=>{setActiveTab(tab.key);if(tab.key==='history')fetchHistory();if(tab.key==='devices')fetchDevices();}}
                style={{padding:'12px 24px',border:'none',cursor:'pointer',fontWeight:600,fontSize:13,background:'transparent',
                  borderBottom:activeTab===tab.key?'3px solid #2D5C3F':'3px solid transparent',
                  color:activeTab===tab.key?'#2D5C3F':'#666'}}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
        {(activeTab==='history' && order) ? (
          <div style={{padding:'20px 24px',maxHeight:'500px',overflowY:'auto'}}>
            {historyLoading ? (
              <div style={{textAlign:'center',padding:40,color:'#999'}}>Loading history...</div>
            ) : historyLogs.length===0 ? (
              <div style={{textAlign:'center',padding:40,color:'#999'}}>No activity recorded yet for this work order</div>
            ) : (
              <div style={{position:'relative',paddingLeft:8}}>
                <div style={{position:'absolute',left:24,top:0,bottom:0,width:2,background:'#e9ecef'}}/>
                {historyLogs.map(log=>(
                  <div key={log.id} style={{display:'flex',gap:16,marginBottom:20,position:'relative'}}>
                    <div style={{width:32,height:32,borderRadius:'50%',flexShrink:0,zIndex:1,background:ACTION_COLORS[log.action]||'#999',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,color:'#fff',fontWeight:700}}>
                      {log.source==='mobile'?'📱':log.performed_by_name?'👤':'⚙️'}
                    </div>
                    <div style={{flex:1,background:'#f8f9fa',borderRadius:8,padding:'10px 14px',border:'1px solid #e9ecef'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <span style={{fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',color:ACTION_COLORS[log.action]||'#555'}}>{log.action?.replace(/_/g,' ')}</span>
                        <span style={{fontSize:11,color:'#999'}}>{formatTs(log.performed_at)}</span>
                      </div>
                      <div style={{fontSize:13,color:'#333',marginTop:4,fontWeight:600}}>
                        {log.performed_by_name||'System'}
                        {log.performed_by_role&&<span style={{fontWeight:400,color:'#666'}}> · {log.performed_by_role}</span>}
                        {log.source&&<span style={{fontWeight:400,color:'#999'}}> · {log.source==='mobile'?'Mobile App':log.source==='system'?'System':'Admin Panel'}</span>}
                      </div>
                      {log.notes && (
                        <div style={{fontSize:12,color:'#666',marginTop:4}}>{log.notes}</div>
                      )}
                      {log.changed_fields && Object.keys(log.changed_fields).length > 0 && (
                        <div style={{marginTop:8, background:'white', borderRadius:6,
                          border:'1px solid #e9ecef', overflow:'hidden'}}>
                          {Object.entries(log.changed_fields).map(([field, val]) => {
                            const labels = {
                              scheduled_date: 'Date', scheduled_time_start: 'Start Time',
                              scheduled_time_end: 'End Time', technician_id: 'Technician',
                              service_type: 'Service Type', priority: 'Priority',
                              status: 'Status', notes: 'Notes'
                            };
                            const label = labels[field] || field.replace(/_/g,' ');
                            const oldVal = val?.old;
                            const newVal = val?.new;
                            return (
                              <div key={field} style={{padding:'4px 10px',
                                borderBottom:'1px solid #f3f4f6',
                                display:'flex', gap:8, alignItems:'center', fontSize:11}}>
                                <span style={{color:'#6B7280', minWidth:90, fontWeight:600}}>
                                  {label}
                                </span>
                                {oldVal !== undefined && (
                                  <>
                                    <span style={{color:'#DC2626', textDecoration:'line-through'}}>
                                      {String(oldVal)}
                                    </span>
                                    <span style={{color:'#9CA3AF'}}>→</span>
                                  </>
                                )}
                                <span style={{color:'#16A34A', fontWeight:600}}>
                                  {String(newVal ?? '')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (activeTab==='devices' && order) ? (
          <div style={{padding:'16px 24px',maxHeight:'500px',overflowY:'auto'}}>
            {devicesLoading ? (
              <div style={{textAlign:'center',padding:40,color:'#999'}}>Loading facility structure...</div>
            ) : deviceTree.length===0 ? (
              <div style={{textAlign:'center',padding:40,color:'#999'}}>No devices found for this facility</div>
            ) : (
              <DeviceTree nodes={deviceTree} level={0} />
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit} style={styles.content}>
          {/* ═══ D.27a Step Header (sequential flow indicator) ═══ */}
          {/* Visual only in D.27a — content remains flat scroll. */}
          {/* D.27b will wrap content in {currentStep === N && (...)} blocks. */}
          <div style={{padding:'14px 20px 10px',borderBottom:'1px solid #e9ecef',background:'#fafafa'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',maxWidth:560,margin:'0 auto'}}>
              {/* Bug-1 followup: stepper aligned with TOTAL_STEPS=3 (real content boundaries) */}
              {[
                {n:1,label:'Service Type'},
                {n:2,label:'Catalog'},
                {n:3,label:'Details'}
              ].map((step,idx,arr)=>{
                const isActive = currentStep === step.n;
                const isCompleted = currentStep > step.n;
                const isSkipped = step.recurringOnly && !isRecurring;
                const isLast = idx === arr.length - 1;
                const fillColor = isSkipped ? '#d1d5db' : (isActive || isCompleted) ? '#2D5C3F' : '#bbb';
                const labelColor = isSkipped ? '#aaa' : isActive ? '#2D5C3F' : '#666';
                return (
                  <React.Fragment key={step.n}>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',flex:'0 0 auto',width:78}}>
                      <div style={{
                        width:30,height:30,borderRadius:'50%',
                        background:fillColor,color:'white',
                        display:'flex',alignItems:'center',justifyContent:'center',
                        fontSize:13,fontWeight:700,
                        opacity:isSkipped?0.55:1,
                        boxShadow:isActive?'0 0 0 3px rgba(45,92,63,0.25)':'none',
                        transition:'background 0.15s'
                      }}>
                        {isCompleted ? '✓' : isSkipped ? '—' : step.n}
                      </div>
                      <div style={{
                        fontSize:10,fontWeight:600,marginTop:6,
                        color:labelColor,textAlign:'center',
                        textDecoration:isSkipped?'line-through':'none',
                        lineHeight:1.2
                      }}>{step.label}</div>
                    </div>
                    {!isLast && (
                      <div style={{
                        flex:1,height:2,
                        background:isCompleted?'#2D5C3F':'#ddd',
                        marginTop:14
                      }}/>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div style={styles.formGrid}>
            {/* ═══ D.27b Step 1: Service Type ═══ */}
            {currentStep === 1 && (<>
            <div style={styles.formGroup}>
              <label style={styles.label}>Service Type *</label>
              <select
                value={formData.service_type}
                onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                style={styles.input}
                required
              >
                <option value="routine">Routine</option>
                <option value="corrective">Corrective</option>
                <option value="emergency">Emergency</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Priority *</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                style={styles.input}
                required
                disabled={order && ['in_progress', 'completed'].includes(order.status)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* D.27c: Recurring toggle removed from Step 1 — isRecurring is now driven entirely by catalog selection in Step 2. Manual entry defaults to punctual. */}
            </>)}

            {/* ═══ D.27c Step 2: Service Selection (Catalog) ═══ */}
            {currentStep === 2 && (<>
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                <label style={styles.label}>Select Service from Catalog</label>
                <select
                  value={catalogItemId}
                  onChange={(e) => {
                    const newId = e.target.value;
                    setCatalogItemId(newId);
                    if (!newId) return;
                    const item = catalogItems.find(c => String(c.id) === newId);
                    if (!item) return;
                    if (item.operational_category) {
                      setFormData(prev => ({ ...prev, service_type: item.operational_category }));
                    }
                    if (item.service_type === 'recurring') {
                      setIsRecurring(true);
                      const freq = item.frequency;
                      setRecurringConfig(prev => {
                        const updates = { ...prev, recurrence_pattern: freq };
                        if (freq === 'daily' && (!prev.days_of_week || prev.days_of_week.length === 0)) {
                          updates.days_of_week = [1, 2, 3, 4, 5];
                        } else if (freq === '2x_week' && (!prev.days_of_week || prev.days_of_week.length !== 2)) {
                          updates.days_of_week = [2, 4];
                        } else if (freq === '3x_week' && (!prev.days_of_week || prev.days_of_week.length !== 3)) {
                          updates.days_of_week = [1, 3, 5];
                        }
                        if (['bimonthly', 'quarterly', 'semiannual'].includes(freq) && !prev.day_of_month) {
                          updates.day_of_month = 1;
                        }
                        return updates;
                      });
                      // D.27c.2: pre-fill price for recurring only if catalog has a non-zero default. Otherwise force conscious entry.
                      if (item.default_price != null && parseFloat(item.default_price) > 0) {
                        setServicePrice(String(item.default_price));
                      } else {
                        setServicePrice('');
                      }
                    } else {
                      setIsRecurring(false);
                      setWoType('service_punctual');
                      // D.27c.2: only pre-fill if default_price > 0
                      if (item.default_price != null && parseFloat(item.default_price) > 0) {
                        setServicePrice(String(item.default_price));
                      } else {
                        setServicePrice('');
                      }
                    }
                  }}
                  style={styles.input}
                >
                  <option value="">— Select a service or use manual entry —</option>
                  <optgroup label="🔁 Recurring services">
                    {catalogItems.filter(c => c.service_type === 'recurring').map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.frequency ? ` — ${c.frequency.replace('_', ' ')}` : ''}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="📍 Punctual services">
                    {catalogItems.filter(c => c.service_type === 'punctual').map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.default_price != null ? ` ($${parseFloat(c.default_price).toFixed(2)})` : ''}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <div style={{fontSize: 11, color: '#888', marginTop: 4}}>
                  Recurring items set up a contract; punctual items are one-time services.
                </div>
              </div>
              {catalogItemId && (() => {
                const item = catalogItems.find(c => String(c.id) === catalogItemId);
                if (!item) return null;
                const itemIsRecurring = item.service_type === 'recurring';
                return (
                  <div style={{
                    gridColumn: '1 / -1',
                    padding: 14,
                    background: itemIsRecurring ? '#EFF6FF' : '#F0FDF4',
                    border: '2px solid ' + (itemIsRecurring ? '#3B82F6' : '#10B981'),
                    borderRadius: 8,
                  }}>
                    <div style={{fontWeight: 700, color: itemIsRecurring ? '#1E40AF' : '#065F46', marginBottom: 6, fontSize: 14}}>
                      {itemIsRecurring ? '🔁 Recurring service' : '📍 Punctual service'}
                      {itemIsRecurring && isUpgrade && (
                        <span style={{marginLeft: 8, padding: '2px 8px', background: '#F59E0B', color: 'white', fontSize: 11, borderRadius: 4, fontWeight: 700}}>
                          UPGRADE
                        </span>
                      )}
                    </div>
                    <div style={{fontSize: 13, color: '#333', fontWeight: 600}}>{item.name}</div>
                    <div style={{fontSize: 12, color: '#666', marginTop: 4}}>
                      Category: {item.operational_category}
                      {itemIsRecurring
                        ? ` · Frequency: ${item.frequency ? item.frequency.replace('_', ' ') : 'TBD'} · Price inherits from contract`
                        : ` · Default price: $${item.default_price ? parseFloat(item.default_price).toFixed(2) : '—'}`}
                    </div>
                  </div>
                );
              })()}
              {!catalogItemId && (
                <div style={{
                  gridColumn: '1 / -1',
                  padding: 12,
                  background: '#F9FAFB',
                  border: '1px dashed #D1D5DB',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#6B7280',
                  textAlign: 'center'
                }}>
                  No catalog item selected — manual entry mode (defaults to Punctual with manual pricing).
                </div>
              )}
            </>)}

            {/* ═══ D.27c Step 3+4+5 catch-all (split in D.27d/e/f) — absorbs old Step 2 content (Date/Time/Tech/Notes) ═══ */}
            {currentStep >= 3 && (<>
            {/* D.27d: Date field hidden for recurring (auto-populated from computed first visit) */}
            {!isRecurring && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Date *</label>
              <input
                type="date"
                required
                value={formData.scheduled_date}
                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                style={styles.input}
                disabled={order && ['in_progress', 'completed'].includes(order.status)}
              />
            </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Estimated Duration (min) *</label>
              <input
                type="number"
                required
                value={formData.estimated_duration}
                onChange={(e) => {
                  const newDuration = parseInt(e.target.value) || 0;
                  if (formData.scheduled_time_start) {
                    const [h, m] = formData.scheduled_time_start.split(':').map(Number);
                    const totalMin = h * 60 + m + newDuration;
                    const endH = Math.floor(totalMin / 60) % 24;
                    const endM = totalMin % 60;
                    const endTime = String(endH).padStart(2,'0') + ':' + String(endM).padStart(2,'0');
                    setFormData({...formData, estimated_duration: newDuration, scheduled_time_end: endTime});
                  } else {
                    setFormData({...formData, estimated_duration: newDuration});
                  }
                }}
                style={styles.input}
                disabled={order && ['in_progress', 'completed'].includes(order.status)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Start Time *</label>
              <input
                type="time"
                required
                value={formData.scheduled_time_start}
                onChange={(e) => handleTimeStartChange(e.target.value)}
                style={styles.input}
                disabled={order && ['in_progress', 'completed'].includes(order.status)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>End Time (auto-calculated)</label>
              <input
                type="time"
                value={formData.scheduled_time_end}
                readOnly
                style={{...styles.input, backgroundColor: '#f5f5f5'}}
              />
              {formData.scheduled_time_start && formData.scheduled_time_end && 
               parseInt(formData.scheduled_time_end.split(':')[0]) < parseInt(formData.scheduled_time_start.split(':')[0]) && (
                <small style={{ color: '#E65100', fontWeight: 'bold', marginTop: '4px', display: 'block' }}>
                  ⚠ Ends next day ({formData.scheduled_date ? new Date(new Date(formData.scheduled_date + 'T12:00:00').getTime() + 86400000).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) : ''})
                </small>
              )}
            </div>

            <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
              <label style={styles.label}>Assign Technician</label>
              <select
                value={formData.technician_id}
                onChange={(e) => setFormData({...formData, technician_id: e.target.value})}
                style={styles.input}
              >
                <option value="">Unassigned</option>
                {technicians.map(tech => (
                  <option key={tech.id} value={tech.id}>
                    {tech.first_name} {tech.last_name}
                  </option>
                ))}
              </select>
            </div>

            {formData.technician_id && (
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                {checkingAvailability && (
                  <div style={{...styles.availabilityStatus, ...styles.statusLoading}}>
                    Checking availability...
                  </div>
                )}
                {!checkingAvailability && availabilityStatus === 'available' && (
                  <div style={{...styles.availabilityStatus, ...styles.statusAvailable}}>
                    ✓ Technician is available
                  </div>
                )}
                {!checkingAvailability && availabilityStatus === 'conflict' && (
                  <div style={{...styles.availabilityStatus, ...styles.statusConflict}}>
                    ⚠ Conflicts found:
                    <ul style={styles.conflictList}>
                      {conflicts.map((c, idx) => (
                        <li key={idx}>{c.order_number}: {c.scheduled_time_start} - {c.scheduled_time_end}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows="3"
                style={styles.textarea}
              />
            </div>

            {/* ═══ PRICING SECTION (Sub-session C) ═══ */}
            {/* D.27c.1: shown for ALL WOs (recurring + punctual). Price is required. */}
            {canViewPricing && (
              <div style={{...styles.formGroup, gridColumn: '1 / -1', marginTop: 8,
                padding: 16, background: '#FAFBF9',
                border: '1px solid #E0E6E0', borderRadius: 8}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                  <strong style={{color: '#1A3A2A', fontSize: 14}}>💰 Pricing</strong>
                  <span style={{fontSize: 11, color: '#888'}}>
                    Required for all WOs · use $0.00 for free services
                  </span>
                </div>

                {/* Catalog dropdown (Sub-session D Step 12 + 18: recurring + punctual with optgroup) */}
                <div style={{marginBottom: 12}}>
                  <label style={{...styles.label, fontSize: 12}}>
                    From Catalog (optional)
                  </label>
                  <select
                    value={catalogItemId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      setCatalogItemId(newId);
                      if (!newId) return;
                      const item = catalogItems.find(c => String(c.id) === newId);
                      if (!item) return;

                      // Autopopulate the WO's service_type from the catalog's operational_category
                      if (item.operational_category) {
                        setFormData(prev => ({ ...prev, service_type: item.operational_category }));
                      }

                      if (item.service_type === 'recurring') {
                        // Recurring item: activate the recurring section and prefill pattern + frequency
                        setIsRecurring(true);
                        const freq = item.frequency;
                        setRecurringConfig(prev => {
                          const updates = { ...prev, recurrence_pattern: freq };
                          // Apply sensible defaults per pattern (same logic as the pattern dropdown handler)
                          if (freq === 'daily' && (!prev.days_of_week || prev.days_of_week.length === 0)) {
                            updates.days_of_week = [1, 2, 3, 4, 5];
                          } else if (freq === '2x_week' && (!prev.days_of_week || prev.days_of_week.length !== 2)) {
                            updates.days_of_week = [2, 4];
                          } else if (freq === '3x_week' && (!prev.days_of_week || prev.days_of_week.length !== 3)) {
                            updates.days_of_week = [1, 3, 5];
                          }
                          if (['bimonthly', 'quarterly', 'semiannual'].includes(freq) && !prev.day_of_month) {
                            updates.day_of_month = 1;
                          }
                          return updates;
                        });
                        // Recurring price comes from contract; clear local field
                        setServicePrice('');
                      } else {
                        // Punctual item: configure for service_punctual WO with default price
                        setWoType('service_punctual');
                        if (item.default_price != null) {
                          setServicePrice(String(item.default_price));
                        }
                      }
                    }}
                    style={{...styles.input, fontSize: 13}}
                  >
                    <option value="">— Manual entry (no catalog item) —</option>
                    <optgroup label="🔁 Recurring services">
                      {catalogItems.filter(c => c.service_type === 'recurring').map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.frequency ? ` — ${c.frequency.replace('_', ' ')}` : ''}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="📍 Punctual services">
                      {catalogItems.filter(c => c.service_type === 'punctual').map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.default_price != null ? ` ($${parseFloat(c.default_price).toFixed(2)})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <div style={{fontSize: 11, color: '#888', marginTop: 4}}>
                    Pick a recurring item to set up a contract, or a punctual item for a one-time service. You can still override below.
                  </div>
                </div>

                {/* D.32: Pricing mode radio (recurring WOs only) */}
                {isRecurring && (
                  <div style={{
                    background: pricingMode === '' ? '#FEF3C7' : '#F9FAFB',
                    border: pricingMode === '' ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 12,
                  }}>
                    <div style={{fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8}}>
                      Pricing Mode {pricingMode === '' && <span style={{color: '#92400E', fontWeight: 'normal'}}>— please select one</span>}
                    </div>
                    <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                      <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13}}>
                        <input
                          type="radio"
                          name="pricingMode"
                          value="total"
                          checked={pricingMode === 'total'}
                          onChange={() => setPricingMode('total')}
                          style={{cursor: 'pointer'}}
                        />
                        <span>{isIntraMonthlyPattern(recurringConfig?.recurrence_pattern) ? 'Monthly Total' : 'Annual Contract Total'}</span>
                      </label>
                      <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13}}>
                        <input
                          type="radio"
                          name="pricingMode"
                          value="individual"
                          checked={pricingMode === 'individual'}
                          onChange={() => setPricingMode('individual')}
                          style={{cursor: 'pointer'}}
                        />
                        <span>Individual per visit</span>
                      </label>
                    </div>
                  </div>
                )}
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12}}>
                  <div>
                    <label style={{...styles.label, fontSize: 12}}>Type</label>
                    <select value={woType} onChange={(e) => setWoType(e.target.value)}
                      style={{...styles.input, fontSize: 13}}>
                      <option value="service_punctual">Service (one-off)</option>
                      <option value="equipment_sale">Equipment sale</option>
                    </select>
                  </div>
                  <div>
                    <label style={{...styles.label, fontSize: 12}}>
                      {/* D.32: dynamic label based on isRecurring + pricingMode */}
                      {(() => {
                        if (lineItems.length > 0) return <>Service Price (USD) <span style={{color: '#888', fontWeight: 'normal'}}>— ignored when line items present</span></>;
                        if (!isRecurring) return 'Service Price (USD)';
                        if (pricingMode === '') return <>Service Price (USD) <span style={{color: '#92400E', fontWeight: 'normal'}}>— pick mode above</span></>;
                        if (pricingMode === 'individual') return 'Service Price per Visit (USD)';
                        return isIntraMonthlyPattern(recurringConfig?.recurrence_pattern)
                          ? 'Monthly Total Price (USD)'
                          : 'Annual Contract Total Price (USD)';
                      })()}
                    </label>
                    <input type="number" min="0" step="0.01"
                      required={lineItems.length === 0}
                      value={servicePrice}
                      onChange={(e) => setServicePrice(e.target.value)}
                      disabled={lineItems.length > 0 || (isRecurring && pricingMode === '')}
                      placeholder={
                        lineItems.length > 0 ? 'Computed from line items' :
                        (isRecurring && pricingMode === '') ? 'Pick a pricing mode first' :
                        '0.00 (required)'
                      }
                      style={{
                        ...styles.input,
                        fontSize: 13,
                        background: (lineItems.length > 0 || (isRecurring && pricingMode === ''))
                          ? '#F0F0F0'
                          : (servicePrice === '' ? '#FEF3C7' : 'white'),
                        border: (lineItems.length === 0 && !(isRecurring && pricingMode === '') && servicePrice === '')
                          ? '2px solid #F59E0B'
                          : undefined
                      }}
                    />
                    {/* D.27c.2: warning when price is empty (force conscious entry) */}
                    {lineItems.length === 0 && servicePrice === '' && (
                      <div style={{fontSize: 11, color: '#92400E', fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4}}>
                        <span>⚠</span> Enter a price (use 0.00 for free services)
                      </div>
                    )}
                  </div>
                </div>

                {/* D.32: Live per-visit computation banner (Total mode only) */}
                {isRecurring && pricingMode === 'total' && servicePrice !== '' && parseFloat(servicePrice) > 0 && (() => {
                  const total = parseFloat(servicePrice);
                  const visits = getPaidVisitsCount(recurringConfig?.recurrence_pattern, recurringConfig?.days_of_week);
                  const perVisit = total / visits;
                  const isIntra = isIntraMonthlyPattern(recurringConfig?.recurrence_pattern);
                  const periodLabel = isIntra ? 'monthly' : 'annual';
                  const visitsLabel = isIntra ? 'paid visits/month' : 'visits/year';
                  return (
                    <div style={{
                      background: '#ECFDF5',
                      border: '1px solid #10B981',
                      borderRadius: 6,
                      padding: 10,
                      marginBottom: 12,
                      fontSize: 13,
                      color: '#065F46',
                    }}>
                      <span style={{fontWeight: 600}}>
                        ${total.toFixed(2)} {periodLabel} ÷ {visits} {visitsLabel} = <span style={{color: '#047857'}}>${perVisit.toFixed(2)} per visit</span>
                      </span>
                    </div>
                  );
                })()}
                {/* Line Items mini-editor */}
                <div>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                    <span style={{fontSize: 12, fontWeight: 600, color: '#444'}}>Line Items (optional)</span>
                    <button type="button"
                      onClick={() => setLineItems([...lineItems, {description: '', quantity: 1, unit_price: 0}])}
                      style={{padding: '4px 10px', fontSize: 12, background: '#2D5C3F', color: 'white',
                        border: 'none', borderRadius: 4, cursor: 'pointer'}}>
                      + Add line
                    </button>
                  </div>
                  {lineItems.length === 0 ? (
                    <div style={{fontSize: 12, color: '#999', padding: '8px 0', fontStyle: 'italic'}}>
                      No line items. Use the field above for a single price, or add lines for itemized billing.
                    </div>
                  ) : (
                    <div>
                      {lineItems.map((li, idx) => (
                        <div key={idx} style={{display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 80px 32px',
                          gap: 6, marginBottom: 6, alignItems: 'center'}}>
                          <input type="text" placeholder="Description"
                            value={li.description}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = {...next[idx], description: e.target.value};
                              setLineItems(next);
                            }}
                            style={{...styles.input, fontSize: 12, padding: '6px 8px'}}
                          />
                          <input type="number" min="0" step="0.01" placeholder="Qty"
                            value={li.quantity}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = {...next[idx], quantity: e.target.value};
                              setLineItems(next);
                            }}
                            style={{...styles.input, fontSize: 12, padding: '6px 8px'}}
                          />
                          <input type="number" min="0" step="0.01" placeholder="Unit price"
                            value={li.unit_price}
                            onChange={(e) => {
                              const next = [...lineItems];
                              next[idx] = {...next[idx], unit_price: e.target.value};
                              setLineItems(next);
                            }}
                            style={{...styles.input, fontSize: 12, padding: '6px 8px'}}
                          />
                          <div style={{fontSize: 12, color: '#444', textAlign: 'right',
                            fontFamily: 'monospace', fontWeight: 600}}>
                            ${((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)).toFixed(2)}
                          </div>
                          <button type="button"
                            onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}
                            style={{background: '#FEE2E2', color: '#991B1B', border: 'none',
                              borderRadius: 4, cursor: 'pointer', fontSize: 14, padding: '4px 0'}}
                            title="Remove">
                            ×
                          </button>
                        </div>
                      ))}
                      <div style={{textAlign: 'right', fontSize: 13, fontWeight: 700,
                        color: '#1A3A2A', marginTop: 6, paddingTop: 6, borderTop: '1px solid #E0E0E0'}}>
                        Total: ${lineItems.reduce((s, li) =>
                          s + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)), 0).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>

                {/* D.27c.1: removed obsolete "auto-calculated from contract" note — price is now editable for all WOs */}
              </div>
            )}

            {/* D.27b: Recurring toggle relocated to Step 1; old block commented out */}
            {/* {!order && (
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>Make this a recurring service</span>
                </label>
              </div>
            )} */}

            {isRecurring && (
              <div style={{...styles.recurringSection, gridColumn: '1 / -1', order: -1}}>
                <h4 style={styles.recurringSectionTitle}>Recurrence Settings</h4>

                {/* D.27d: First visit banner — auto-computed from pattern + start_date + day */}
                {firstVisitFormatted ? (
                  <div style={{
                    background: '#ECFDF5',
                    border: '2px solid #10B981',
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10
                  }}>
                    <span style={{fontSize: 22}}>📅</span>
                    <div style={{flex: 1}}>
                      <div style={{fontSize: 11, color: '#065F46', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5}}>
                        First visit (auto-computed)
                      </div>
                      <div style={{fontSize: 14, color: '#064E3B', fontWeight: 700, marginTop: 2}}>
                        {firstVisitFormatted}
                      </div>
                      <div style={{fontSize: 11, color: '#047857', marginTop: 4}}>
                        Technician availability will validate against this date below.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: '#FEF3C7',
                    border: '1px solid #FBBF24',
                    borderRadius: 6,
                    padding: '10px 12px',
                    marginBottom: 16,
                    fontSize: 12,
                    color: '#92400E'
                  }}>
                    📅 Set the recurrence pattern + start date below to compute the first visit.
                  </div>
                )}

                {/* ─── Sub-session D Step 13: prominent is_upgrade checkbox ─── */}
                <div style={{
                  background: isUpgrade ? '#FEF3C7' : '#EFF6FF',
                  border: '2px solid ' + (isUpgrade ? '#F59E0B' : '#3B82F6'),
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 16,
                }}>
                  {/* D.26: Two clear radio buttons instead of confusing checkbox */}
                  <div style={{fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10}}>
                    Service classification *
                  </div>
                  <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: 10, borderRadius: 6,
                      border: '2px solid ' + (!isUpgrade ? '#3B82F6' : '#E5E7EB'),
                      background: !isUpgrade ? '#EFF6FF' : '#FFF',
                    }}>
                      <input
                        type="radio"
                        name="upgrade_classification"
                        checked={!isUpgrade}
                        onChange={() => setIsUpgrade(false)}
                        style={{marginTop: 3, width: 18, height: 18, cursor: 'pointer', accentColor: '#3B82F6'}}
                      />
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 700, color: '#1E40AF', fontSize: 14, marginBottom: 4}}>
                          📋 Contract Base
                        </div>
                        <div style={{fontSize: 12, color: '#555', lineHeight: 1.5}}>
                          The customer's primary recurring service. Most customers have only one of these.
                        </div>
                        {clientHasRecurring && !isUpgrade && (
                          <div style={{marginTop: 6, padding: 6, background: '#FEE2E2', borderRadius: 4, color: '#991B1B', fontSize: 11, fontWeight: 600}}>
                            ⚠ Heads up: this facility already has an active recurring contract. Are you sure this is also a contract base, not an upgrade?
                          </div>
                        )}
                      </div>
                    </label>
                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: 10, borderRadius: 6,
                      border: '2px solid ' + (isUpgrade ? '#F59E0B' : '#E5E7EB'),
                      background: isUpgrade ? '#FEF3C7' : '#FFF',
                    }}>
                      <input
                        type="radio"
                        name="upgrade_classification"
                        checked={isUpgrade}
                        onChange={() => setIsUpgrade(true)}
                        style={{marginTop: 3, width: 18, height: 18, cursor: 'pointer', accentColor: '#F59E0B'}}
                      />
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 700, color: '#92400E', fontSize: 14, marginBottom: 4}}>
                          🔼 Upgrade Service
                        </div>
                        <div style={{fontSize: 12, color: '#555', lineHeight: 1.5}}>
                          An additional recurring service on top of the contract base. Shows separately on facility detail and can be stopped independently.
                        </div>
                        {!clientHasRecurring && isUpgrade && (
                          <div style={{marginTop: 6, padding: 6, background: '#FEE2E2', borderRadius: 4, color: '#991B1B', fontSize: 11, fontWeight: 600}}>
                            ⚠ Heads up: this facility has no active contract base yet. An upgrade typically goes ON TOP of an existing contract.
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Pattern *</label>
                    <select
                      value={recurringConfig.recurrence_pattern}
                      onChange={(e) => {
                        const p = e.target.value;
                        // When switching to a custom-days pattern, set sensible defaults
                        const updates = { recurrence_pattern: p };
                        if (p === 'daily' && (!recurringConfig.days_of_week || recurringConfig.days_of_week.length === 0)) {
                          updates.days_of_week = [1, 2, 3, 4, 5]; // Mon-Fri
                        } else if (p === '2x_week' && (!recurringConfig.days_of_week || recurringConfig.days_of_week.length !== 2)) {
                          updates.days_of_week = [2, 4]; // Tue, Thu
                        } else if (p === '3x_week' && (!recurringConfig.days_of_week || recurringConfig.days_of_week.length !== 3)) {
                          updates.days_of_week = [1, 3, 5]; // Mon, Wed, Fri
                        }
                        if (['bimonthly', 'quarterly', 'semiannual'].includes(p) && !recurringConfig.day_of_month) {
                          updates.day_of_month = 1;
                        }
                        setRecurringConfig({...recurringConfig, ...updates});
                      }}
                      style={styles.input}
                    >
                      <optgroup label="High frequency">
                        <option value="daily">Daily</option>
                        <option value="3x_week">3× / Week</option>
                        <option value="2x_week">2× / Week</option>
                      </optgroup>
                      <optgroup label="Standard">
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                      </optgroup>
                      <optgroup label="Low frequency">
                        <option value="bimonthly">Bi-Monthly (every 2 months)</option>
                        <option value="quarterly">Quarterly (every 3 months)</option>
                        <option value="semiannual">Semi-Annual (every 6 months)</option>
                      </optgroup>
                    </select>
                  </div>

                  {['weekly', 'biweekly', 'monthly'].includes(recurringConfig.recurrence_pattern) && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Day of Week *</label>
                      <select
                        value={recurringConfig.day_of_week}
                        onChange={(e) => setRecurringConfig({...recurringConfig, day_of_week: parseInt(e.target.value)})}
                        style={styles.input}
                      >
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </select>
                    </div>
                  )}
                  {/* Sub-session D Step 17: days-of-week checkbox group for daily/2x/3x patterns */}
                  {['daily', '2x_week', '3x_week'].includes(recurringConfig.recurrence_pattern) && (
                    <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                      <label style={styles.label}>
                        Days of Week *
                        {recurringConfig.recurrence_pattern === '2x_week' && ' (pick exactly 2)'}
                        {recurringConfig.recurrence_pattern === '3x_week' && ' (pick exactly 3)'}
                        {recurringConfig.recurrence_pattern === 'daily' && ' (default Mon-Fri; expand or reduce as needed)'}
                      </label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                        {[
                          { v: 1, label: 'Mon' },
                          { v: 2, label: 'Tue' },
                          { v: 3, label: 'Wed' },
                          { v: 4, label: 'Thu' },
                          { v: 5, label: 'Fri' },
                          { v: 6, label: 'Sat' },
                          { v: 0, label: 'Sun' },
                        ].map(d => {
                          const checked = (recurringConfig.days_of_week || []).includes(d.v);
                          return (
                            <label key={d.v}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 12px',
                                border: '2px solid ' + (checked ? '#2D5C3F' : '#E5E7EB'),
                                background: checked ? '#ECFDF5' : '#FFF',
                                borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                color: checked ? '#1A3A2A' : '#6B7280',
                              }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const cur = recurringConfig.days_of_week || [];
                                  let next;
                                  if (e.target.checked) {
                                    next = [...cur, d.v].sort((a, b) => a - b);
                                  } else {
                                    next = cur.filter(x => x !== d.v);
                                  }
                                  setRecurringConfig({...recurringConfig, days_of_week: next});
                                }}
                                style={{ margin: 0 }}
                              />
                              {d.label}
                            </label>
                          );
                        })}
                      </div>
                      {recurringConfig.recurrence_pattern === '2x_week' && (recurringConfig.days_of_week || []).length !== 2 && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                          ⚠ 2× / Week requires exactly 2 days selected (currently {(recurringConfig.days_of_week || []).length}).
                        </div>
                      )}
                      {recurringConfig.recurrence_pattern === '3x_week' && (recurringConfig.days_of_week || []).length !== 3 && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                          ⚠ 3× / Week requires exactly 3 days selected (currently {(recurringConfig.days_of_week || []).length}).
                        </div>
                      )}
                      {recurringConfig.recurrence_pattern === 'daily' && (recurringConfig.days_of_week || []).length === 0 && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                          ⚠ Pick at least 1 day.
                        </div>
                      )}
                    </div>
                  )}
                  {/* Sub-session D Step 17: day-of-month for bimonthly/quarterly/semiannual */}
                  {['bimonthly', 'quarterly', 'semiannual'].includes(recurringConfig.recurrence_pattern) && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Day of Month *
                        {recurringConfig.recurrence_pattern === 'bimonthly' && ' (every 2 months)'}
                        {recurringConfig.recurrence_pattern === 'quarterly' && ' (every 3 months)'}
                        {recurringConfig.recurrence_pattern === 'semiannual' && ' (every 6 months)'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={recurringConfig.day_of_month}
                        onChange={(e) => setRecurringConfig({...recurringConfig, day_of_month: parseInt(e.target.value) || 1})}
                        style={styles.input}
                      />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        Days 29-31 will adjust automatically when the month is shorter (e.g., February).
                      </div>
                    </div>
                  )}

                  {recurringConfig.recurrence_pattern === 'biweekly' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Weeks *</label>
                      <select
                        value={recurringConfig.biweekly_option}
                        onChange={(e) => setRecurringConfig({...recurringConfig, biweekly_option: e.target.value})}
                        style={styles.input}
                      >
                        <option value="1_3">Weeks 1 & 3</option>
                        <option value="2_4">Weeks 2 & 4</option>
                      </select>
                    </div>
                  )}

                  {recurringConfig.recurrence_pattern === 'monthly' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Week of Month *</label>
                      <select
                        value={recurringConfig.week_of_month}
                        onChange={(e) => setRecurringConfig({...recurringConfig, week_of_month: parseInt(e.target.value)})}
                        style={styles.input}
                      >
                        <option value="1">1st week</option>
                        <option value="2">2nd week</option>
                        <option value="3">3rd week</option>
                        <option value="4">4th week</option>
                        <option value="-1">Last week</option>
                      </select>
                    </div>
                  )}

                  <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                    <label style={styles.label}>Recurrence Start Date *</label>
                    <input
                      type="date"
                      required
                      value={recurringConfig.recurrence_start_date}
                      onChange={(e) => setRecurringConfig({...recurringConfig, recurrence_start_date: e.target.value})}
                      style={styles.input}
                    />
                    <small style={styles.hint}>
                      The initial service above (on {formData.scheduled_date}) will be created first.
                      The recurring pattern will start from this date.
                    </small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>End Date (optional)</label>
                    <input
                      type="date"
                      value={recurringConfig.end_date}
                      onChange={(e) => setRecurringConfig({...recurringConfig, end_date: e.target.value})}
                      style={styles.input}
                    />
                    <small style={styles.hint}>Leave empty for no end date</small>
                  </div>

                  {/* VIP 5th Week Toggle — only relevant for weekly/2x/3x/daily */}
                  {['weekly','2x_week','3x_week','daily'].includes(recurringConfig.recurrence_pattern) && (
                    <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                      <div style={{background: recurringConfig.fifth_week_vip ? '#FEF9C3' : '#F9FAFB', border: '1px solid ' + (recurringConfig.fifth_week_vip ? '#FDE047' : '#E5E7EB'), borderRadius: '8px', padding: '14px', display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                        <input
                          type="checkbox"
                          id="fifth_week_vip"
                          checked={recurringConfig.fifth_week_vip}
                          onChange={e => setRecurringConfig({...recurringConfig, fifth_week_vip: e.target.checked})}
                          style={{width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer', accentColor: '#CA8A04'}}
                        />
                        <label htmlFor="fifth_week_vip" style={{cursor: 'pointer'}}>
                          <div style={{fontSize: '13px', fontWeight: 700, color: recurringConfig.fifth_week_vip ? '#92400E' : '#374151'}}>
                            ⭐ VIP Client — 5th Week Complimentary Visit
                          </div>
                          <div style={{fontSize: '12px', color: '#6B7280', marginTop: '3px'}}>
                            {recurringConfig.fifth_week_vip
                              ? 'When a month has a 5th occurrence, a WO will be created marked as non-billable (complimentary service).'
                              : 'Standard: when a month has a 5th occurrence, no WO will be generated for that week.'}
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {previewDates.length > 0 && (
                    <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                      <label style={styles.label}>Preview - Recurring services starting {new Date(recurringConfig.recurrence_start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}:</label>
                      <div style={styles.previewBox}>
                        {previewDates.map((date, idx) => (
                          <div key={idx} style={styles.previewDate}>
                            {idx === 0 ? '✓ ' : '→ '}{date}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            </>)}
          </div>

          <div style={styles.actions}>
            <div style={styles.leftActions}>
              {order && order.status === 'scheduled' && (
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  style={styles.cancelOrderBtn}
                >
                  Cancel Order
                </button>
              )}
            </div>
            <div style={styles.rightActions}>
              {/* ═══ D.27b Step Navigation ═══ */}
              <button
                type="button"
                onClick={onClose}
                style={styles.closeButton}
              >
                Cancel
              </button>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(getPrevStep(currentStep))}
                  style={styles.closeButton}
                >
                  ← Back
                </button>
              )}
              {currentStep < TOTAL_STEPS && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(getNextStep(currentStep))}
                  style={styles.submitBtn}
                >
                  Next →
                </button>
              )}
              {currentStep === TOTAL_STEPS && (
                <button
                  type="submit"
                  disabled={submitting || (formData.technician_id && availabilityStatus === 'conflict')}
                  style={submitting || (formData.technician_id && availabilityStatus === 'conflict') ?
                    {...styles.submitBtn, ...styles.submitBtnDisabled} : styles.submitBtn}
                >
                  {submitting ? 'Saving...' : (order ? (order.status === 'in_progress' ? 'Update Notes Only' : (order.status === 'completed' ? 'View Only' : 'Update Order')) : 'Create Order')}
                </button>
              )}
            </div>
          </div>
        </form>
        )}
      </div>

      {/* Sub-session D Step 14c: Stop Recurring modal */}
      <StopRecurringModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onSuccess={() => { setShowStopModal(false); onSuccess && onSuccess(); onClose && onClose(); }}
        recurringService={recurringServiceData}
      />

      {showConfirmation && (
        <RecurringConfirmationModal
          initialDate={new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
          recurrenceStartDate={recurringConfig.recurrence_start_date}
          currentMonthDates={currentMonthDates}
          endDate={recurringConfig.end_date}
          onConfirm={executeCreate}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
      {/* Bug-1 followup: punctual confirmation summary modal */}
      {showPunctualConfirm && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:8,padding:24,maxWidth:480,width:'90%',maxHeight:'90vh',overflow:'auto',boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:16,fontWeight:700,color:'#2D5C3F',marginBottom:16,paddingBottom:8,borderBottom:'1px solid #e9ecef'}}>
              Confirm Work Order Creation
            </div>
            <div style={{display:'grid',gridTemplateColumns:'130px 1fr',gap:'8px 12px',fontSize:13,marginBottom:12}}>
              <div style={{color:'#666'}}>Service Type:</div>
              <div style={{fontWeight:600,textTransform:'capitalize'}}>{formData.service_type || '—'}</div>
              <div style={{color:'#666'}}>Priority:</div>
              <div style={{fontWeight:600,textTransform:'capitalize'}}>{formData.priority || 'normal'}</div>
              {catalogItemId && Array.isArray(catalogItems) && (() => {
                const ci = catalogItems.find(c => c.id === parseInt(catalogItemId,10));
                return ci ? (<React.Fragment><div style={{color:'#666'}}>Catalog:</div><div style={{fontWeight:600}}>{ci.name}</div></React.Fragment>) : null;
              })()}
              <div style={{color:'#666'}}>Date:</div>
              <div style={{fontWeight:600}}>
                {formData.scheduled_date ? new Date(formData.scheduled_date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',year:'numeric',month:'short',day:'numeric'}) : '—'}
              </div>
              <div style={{color:'#666'}}>Time:</div>
              <div style={{fontWeight:600}}>
                {formData.scheduled_time_start || '—'}
                {formData.estimated_duration ? ` (${formData.estimated_duration} min)` : ''}
              </div>
              <div style={{color:'#666'}}>Technician:</div>
              <div style={{fontWeight:600}}>
                {(() => {
                  if (!Array.isArray(technicians)) return '—';
                  const t = technicians.find(t => t.id === parseInt(formData.technician_id,10));
                  return t ? `${t.first_name || ''} ${t.last_name || ''}`.trim() : '—';
                })()}
              </div>
              {lineItems.length > 0 ? (
                <React.Fragment>
                  <div style={{color:'#666'}}>Line Items:</div>
                  <div style={{fontWeight:600}}>
                    {lineItems.filter(li => (li.description||'').trim() !== '').length} item(s)
                  </div>
                </React.Fragment>
              ) : (servicePrice !== '' && !isNaN(parseFloat(servicePrice)) && (
                <React.Fragment>
                  <div style={{color:'#666'}}>Price:</div>
                  <div style={{fontWeight:600,color:'#2D5C3F'}}>${parseFloat(servicePrice).toFixed(2)}</div>
                </React.Fragment>
              ))}
            </div>
            {formData.notes && formData.notes.trim() !== '' && (
              <div style={{background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:6,padding:10,marginBottom:12,fontSize:12,color:'#374151'}}>
                <div style={{fontWeight:600,marginBottom:4,color:'#666',fontSize:11}}>NOTES</div>
                <div style={{whiteSpace:'pre-wrap',lineHeight:1.4}}>
                  {formData.notes.length > 240 ? formData.notes.slice(0,240) + '…' : formData.notes}
                </div>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:16,paddingTop:12,borderTop:'1px solid #e9ecef'}}>
              <button type="button" onClick={() => setShowPunctualConfirm(false)}
                style={{padding:'8px 16px',background:'#f3f4f6',border:'1px solid #d1d5db',borderRadius:6,cursor:'pointer',fontWeight:500,fontSize:13}}>
                Cancel
              </button>
              <button type="button" disabled={submitting}
                onClick={() => { setShowPunctualConfirm(false); executeCreate(); }}
                style={{padding:'8px 16px',background:submitting?'#9ca3af':'#2D5C3F',color:'white',border:'none',borderRadius:6,cursor:submitting?'not-allowed':'pointer',fontWeight:600,fontSize:13}}>
                {submitting ? 'Saving…' : 'Confirm Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmCancel && (
        <ConfirmDialog
          message="Are you sure you want to cancel this service order? This cannot be undone."
          confirmText="Yes, Cancel Order"
          confirmColor="#DC2626"
          icon="🚫"
          onConfirm={doCancelOrder}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
    </div>

  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#2D5C3F',
    color: 'white',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  title: {
    fontSize: '20px',
    margin: 0,
    fontWeight: '600'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 10px'
  },
  clientInfo: {
    padding: '12px 20px',
    backgroundColor: '#F0F7F4',
    borderBottom: '1px solid #E0E0E0',
    fontSize: '14px',
    color: '#2D5C3F'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '6px',
    color: '#333'
  },
  input: {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  textarea: {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  availabilityStatus: {
    marginTop: '10px',
    padding: '10px',
    borderRadius: '6px',
    fontSize: '13px'
  },
  statusLoading: {
    backgroundColor: '#F5F5F5',
    color: '#666'
  },
  statusAvailable: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32'
  },
  statusConflict: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
    border: '1px solid #FFB300'
  },
  conflictList: {
    marginTop: '8px',
    paddingLeft: '20px',
    fontSize: '12px'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    padding: '20px',
    borderTop: '1px solid #f0f0f0',
    backgroundColor: '#FAFAFA'
  },
  leftActions: {
    flex: 1
  },
  rightActions: {
    display: 'flex',
    gap: '10px'
  },
  cancelOrderBtn: {
    padding: '12px 20px',
    backgroundColor: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  },
  closeButton: {
    padding: '12px 20px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  },
  submitBtn: {
    padding: '12px 30px',
    backgroundColor: '#D4AF37',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  },
  submitBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  checkboxText: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#2D5C3F'
  },
  recurringSection: {
    backgroundColor: '#F0F7F4',
    border: '2px solid #2D5C3F',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '10px'
  },
  recurringSectionTitle: {
    fontSize: '18px',
    color: '#2D5C3F',
    marginTop: 0,
    marginBottom: '15px'
  },
  hint: {
    fontSize: '12px',
    color: '#666',
    fontStyle: 'italic',
    display: 'block',
    marginTop: '5px'
  },
  previewBox: {
    backgroundColor: '#F0F7F4',
    border: '1px solid #2D5C3F',
    borderRadius: '8px',
    padding: '15px',
    marginTop: '10px'
  },
  previewDate: {
    fontSize: '14px',
    color: '#2D5C3F',
    padding: '8px',
    borderBottom: '1px solid #E0E0E0'
  }
};

export default ServiceOrderModal;
